import React, { useRef, useEffect, useMemo } from 'react'
import styled from '@emotion/styled'
import { IPropertyCard } from '@/components/templates/search/interfaces'
import { BottomSheetSnap } from './MobileBottomSheet'
import { useAppSelector } from '@/app/hooks'
import { selectIsFavorite } from '@/app/reducers/favorites'
import { FavoriteButton } from '@/components/atoms/favoriteButton/FavoriteButton'
import { browserSupportsWebp, preferWebpExtension } from '@/utils/url'

interface MiniPropertyCardProps {
	property: IPropertyCard
	onClose: () => void
	onOpenDetail?: () => void
	onFavoriteClick?: (property: IPropertyCard) => void
	snap?: BottomSheetSnap
	onSwipeLeft?: () => boolean
	onSwipeRight?: () => boolean
}

const cloudfrontUrl = process.env.NEXT_PUBLIC_CLOUDFRONT_IMG || ''
const FALLBACK_IMG = `${cloudfrontUrl}listaseo/img/imagen-no-viene-cards-vertical.png`

const BedIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
		<path d="M2 4v16" /><path d="M2 8h18a2 2 0 0 1 2 2v10" /><path d="M2 17h20" /><path d="M6 8v9" />
	</svg>
)

const BathIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
		<path d="M9 6 6.5 3.5a1.5 1.5 0 0 0-1-.5C4.683 3 4 3.683 4 4.5V17a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5" /><line x1="10" x2="8" y1="5" y2="7" /><line x1="2" x2="22" y1="12" y2="12" />
	</svg>
)

const AreaIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
		<path d="M21 3H3v18h18V3z" /><path d="m9 9 6 6" /><path d="M9 15V9h6" />
	</svg>
)

const ArrowIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
		<path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
	</svg>
)

const SWIPE_THRESHOLD = 80
const FLY_OUT_MS = 150
const FLY_IN_MS = 220
const SWIPE_HINT_DELAY_MS = 700
const SWIPE_HINT_DURATION_MS = 900
const SWIPE_HINT_STORAGE_KEY = 'toctoc:listaSeoV2:mapCardSwipeHintShown'

export const MiniPropertyCard = ({ property, onClose, onOpenDetail, onFavoriteClick, snap, onSwipeLeft, onSwipeRight }: MiniPropertyCardProps) => {
	const isFavorite = useAppSelector(selectIsFavorite(property.idProperty))
	const supportsWebp = useMemo(() => browserSupportsWebp(), [])
	const rawThumbnailSrc = property.imagenPrincipal?.src
	const thumbnailSrc = rawThumbnailSrc
		? (supportsWebp ? preferWebpExtension(rawThumbnailSrc) : rawThumbnailSrc)
		: FALLBACK_IMG
	const cardRef = useRef<HTMLDivElement>(null)
	const startXRef = useRef(0)
	const isDraggingRef = useRef(false)
	const didDragRef = useRef(false)
	const swipeDirRef = useRef<'left' | 'right' | null>(null)

	// Cuando cambia la propiedad (tras un swipe exitoso): anima la entrada desde el lado contrario
	useEffect(() => {
		const card = cardRef.current
		if (!card || !swipeDirRef.current) return
		const dir = swipeDirRef.current
		swipeDirRef.current = null
		const enterFrom = dir === 'left' ? window.innerWidth : -window.innerWidth
		const t = setTimeout(() => {
			if (!cardRef.current) return
			cardRef.current.style.transition = 'none'
			cardRef.current.style.transform = `translateX(${enterFrom}px)`
			cardRef.current.style.opacity = '0'
			requestAnimationFrame(() => requestAnimationFrame(() => {
				if (!cardRef.current) return
				cardRef.current.style.transition = `transform ${FLY_IN_MS}ms ease-out, opacity ${FLY_IN_MS * 0.8}ms ease-out`
				cardRef.current.style.transform = 'translateX(0)'
				cardRef.current.style.opacity = '1'
			}))
		}, FLY_OUT_MS)
		return () => clearTimeout(t)
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [property.idProperty])

	// Enseña que la card se puede deslizar: una animación sutil, una sola vez por sesión de navegador
	useEffect(() => {
		let alreadyShown = true
		try {
			alreadyShown = window.sessionStorage.getItem(SWIPE_HINT_STORAGE_KEY) === '1'
		} catch {
			alreadyShown = true // storage no disponible (modo privado, etc.): no insistir
		}
		if (alreadyShown) return

		const t = setTimeout(() => {
			if (!cardRef.current) return
			try { window.sessionStorage.setItem(SWIPE_HINT_STORAGE_KEY, '1') } catch { /* noop */ }
			cardRef.current.style.animation = `miniCardSwipeHint ${SWIPE_HINT_DURATION_MS}ms ease-out`
		}, SWIPE_HINT_DELAY_MS)
		return () => clearTimeout(t)
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const handleTouchStart = (e: React.TouchEvent) => {
		startXRef.current = e.touches[0].clientX
		isDraggingRef.current = true
		didDragRef.current = false
		if (cardRef.current) {
			cardRef.current.style.transition = 'none'
			cardRef.current.style.animation = 'none'
		}
	}

	const handleTouchMove = (e: React.TouchEvent) => {
		if (!isDraggingRef.current || !cardRef.current) return
		const dx = e.touches[0].clientX - startXRef.current
		if (Math.abs(dx) > 10) didDragRef.current = true
		cardRef.current.style.transform = `translateX(${dx}px)`
		cardRef.current.style.opacity = `${Math.max(0.45, 1 - Math.abs(dx) / 260)}`
	}

	const handleTouchEnd = (e: React.TouchEvent) => {
		if (!isDraggingRef.current || !cardRef.current) return
		isDraggingRef.current = false
		const card = cardRef.current
		const dx = e.changedTouches[0].clientX - startXRef.current

		if (Math.abs(dx) >= SWIPE_THRESHOLD) {
			const isLeft = dx < 0
			const navigated = isLeft ? onSwipeLeft?.() : onSwipeRight?.()
			if (navigated) {
				swipeDirRef.current = isLeft ? 'left' : 'right'
				card.style.transition = `transform ${FLY_OUT_MS}ms ease-in, opacity ${FLY_OUT_MS}ms ease-in`
				card.style.transform = `translateX(${isLeft ? -window.innerWidth : window.innerWidth}px)`
				card.style.opacity = '0'
			} else {
				// Límite alcanzado: efecto rebote
				card.style.transition = 'transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.25s ease'
				card.style.transform = 'translateX(0)'
				card.style.opacity = '1'
			}
		} else {
			// Swipe corto: volver al centro
			card.style.transition = 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.25s ease'
			card.style.transform = 'translateX(0)'
			card.style.opacity = '1'
		}
	}

	const uf = property.precios?.find(p => p.prefix === 'UF')
	const clp = property.precios?.find(p => p.prefix !== 'UF')

	const priceUF = uf ? `${uf.prefix} ${uf.value}` : ''
	const priceCLP = clp ? `${clp.prefix} ${clp.value}` : ''

	const dorm = property.dormitorios?.filter(Boolean)
	const ban = property.bannos?.filter(Boolean)
	const sup = property.superficie?.filter(Boolean)

	const dormText = dorm?.length ? dorm.join('-') : null
	const banText = ban?.length ? ban.join('-') : null
	const supText = sup?.length ? `${sup.join('-')} m²` : null

	const handleCardClick = () => {
		if (didDragRef.current) {
			didDragRef.current = false
			return
		}
		onOpenDetail?.()
	}

	return (
		<Card
			ref={cardRef}
			$snap={snap}
			onClick={handleCardClick}
			onTouchStart={handleTouchStart}
			onTouchMove={handleTouchMove}
			onTouchEnd={handleTouchEnd}
			onAnimationEnd={(e) => { e.currentTarget.style.animation = '' }}
		>
			<ThumbnailWrapper>
				<Thumbnail
					src={thumbnailSrc}
					alt={property.imagenPrincipal?.alt || property.titulo}
					onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK_IMG }}
				/>
				<FavoriteButton
					isFavorite={isFavorite}
					onClick={(e) => { e.stopPropagation(); onFavoriteClick?.(property) }}
					variant="overlay"
					style={{ position: 'absolute', top: 8, left: 8, zIndex: 2 }}
				/>
			</ThumbnailWrapper>
			<Info>
				<PriceLine>
					<PriceUF>{priceUF}</PriceUF>
					{priceCLP && <PriceCLP> / {priceCLP}</PriceCLP>}
				</PriceLine>
				<Address title={property.titulo}>{property.titulo}</Address>
				{property.comuna && <Comuna>{property.comuna}</Comuna>}
				{(dormText || banText || supText) && (
					<Details>
						{dormText && <DetailItem><BedIcon />{dormText}</DetailItem>}
						{banText && <DetailItem><BathIcon />{banText}</DetailItem>}
						{supText && <DetailItem><AreaIcon />{supText}</DetailItem>}
					</Details>
				)}
			</Info>
			<RightCol>
				<CloseBtn onClick={(e) => { e.stopPropagation(); onClose() }} aria-label="Cerrar">✕</CloseBtn>
				{onOpenDetail ? (
					<VerMasBtn onClick={(e) => { e.stopPropagation(); onOpenDetail() }} aria-label="Ver detalle">
						<ArrowIcon />
					</VerMasBtn>
				) : (
					<VerMas
						href={property.urlFicha}
						target="_blank"
						rel="noopener noreferrer"
						aria-label="Ver propiedad"
						onClick={(e) => e.stopPropagation()}
					>
						<ArrowIcon />
					</VerMas>
				)}
			</RightCol>
		</Card>
	)
}

/* ─── Styled ─────────────────────────────────────── */

const BOTTOM_BY_SNAP: Record<BottomSheetSnap, string> = {
	collapsed: 'calc(80px + env(safe-area-inset-bottom) + 8px)',
	half:      'calc(50vh + 8px)',
	full:      'calc(50vh + 8px)', // oculto vía display:none, valor irrelevante
}

const swipeHintKeyframes = `
	@keyframes miniCardSwipeHint {
		0%   { transform: translateX(0); }
		20%  { transform: translateX(-18px); }
		40%  { transform: translateX(4px); }
		60%  { transform: translateX(-9px); }
		80%  { transform: translateX(2px); }
		100% { transform: translateX(0); }
	}
`

const Card = styled.div<{ $snap?: BottomSheetSnap }>`
	${swipeHintKeyframes}
	position: fixed;
	bottom: ${({ $snap }) => BOTTOM_BY_SNAP[$snap ?? 'collapsed']};
	left: 16px;
	right: 16px;
	z-index: 500;
	transition: bottom 0.35s cubic-bezier(0.32, 0.72, 0, 1);

	@media (min-width: 768px) {
		display: none;
	}
	display: ${({ $snap }) => ($snap === 'full' ? 'none' : 'flex')};
	align-items: stretch;
	background: #fff;
	border-radius: 12px;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.18);
	overflow: hidden;
	min-height: 90px;
	cursor: pointer;
`

const ThumbnailWrapper = styled.div`
	position: relative;
	flex-shrink: 0;
`

const Thumbnail = styled.img`
	width: 110px;
	min-height: 90px;
	object-fit: cover;
	display: block;
`

const Info = styled.div`
	flex: 1;
	padding: 8px 10px;
	display: flex;
	flex-direction: column;
	justify-content: center;
	overflow: hidden;
	gap: 2px;
	min-width: 0;
`

const PriceLine = styled.div`
	display: flex;
	align-items: baseline;
	gap: 0;
	flex-wrap: nowrap;
	overflow: hidden;
`

const PriceUF = styled.span`
	font-family: nunito, sans-serif;
	font-size: 13px;
	font-weight: 800;
	color: #3200c1;
	white-space: nowrap;
`

const PriceCLP = styled.span`
	font-family: nunito, sans-serif;
	font-size: 11px;
	font-weight: 600;
	color: #666;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`

const Address = styled.p`
	margin: 0;
	font-family: nunito, sans-serif;
	font-size: 12px;
	font-weight: 600;
	color: #1a1a2e;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`

const Comuna = styled.p`
	margin: 0;
	font-family: nunito, sans-serif;
	font-size: 11px;
	font-weight: 500;
	color: #888;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`

const Details = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 1px;
`

const DetailItem = styled.span`
	display: flex;
	align-items: center;
	gap: 3px;
	font-family: nunito, sans-serif;
	font-size: 11px;
	color: #666;
	white-space: nowrap;
`

const RightCol = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: space-between;
	padding: 8px 6px;
	flex-shrink: 0;
`

const CloseBtn = styled.button`
	background: none;
	border: none;
	font-size: 14px;
	color: #bbb;
	cursor: pointer;
	padding: 0;
	line-height: 1;

	&:active { color: #333; }
`

const VerMas = styled.a`
	display: flex;
	align-items: center;
	justify-content: center;
	color: #3200c1;
	text-decoration: none;

	&:active { opacity: 0.7; }
`

const VerMasBtn = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	color: #3200c1;
	background: none;
	border: none;
	cursor: pointer;
	padding: 0;

	&:active { opacity: 0.7; }
`
